import { useEffect, useState, useRef } from "react";
import { Employee, PersonaExpanded } from "winslow-core";
import ModalHRISRequired from "../collections/ModalHRISRequired";

import "./CirclePersonaEmployeeSelect.css";

import config from "environment";

type CirclePersonaEmployeeSelectProps = {
  onChange: (value: string, isPersona: boolean) => void;
  includeNoFilter?: boolean;
  selectedEmployee?: string;
  selectedPersona?: string;

  presetEmployeeId?: string;
  employees?: Employee[];
  personas?: PersonaExpanded[];
  disabled?: boolean;
};

export const CirclePersonaEmployeeSelect = ({
  onChange,
  selectedEmployee,
  selectedPersona,

  includeNoFilter = false,
  presetEmployeeId,
  employees,
  personas,
  disabled = false,
}: CirclePersonaEmployeeSelectProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState<{
    value: string;
    label: string;
    imageUrl?: string;
    isPersona: boolean;
  } | null>(null);

  const selectorRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [showTooltip, setShowTooltip] = useState<boolean>(false);

  const [showHRISRequired, setShowHRISRequired] = useState<boolean>(false);

  // Add effect to handle presetEmployeeId
  useEffect(() => {
    if (presetEmployeeId && employees && employees.length > 0) {
      const employee = employees.find((e) => e.id === presetEmployeeId);
      if (employee) {
        onChange(presetEmployeeId, false);
        setSelectedOption({
          value: employee.id,
          label: employee.displayName || employee.email,
          imageUrl: employee.avatar || undefined,
          isPersona: false,
        });
      }
    }
  }, [presetEmployeeId, employees, onChange]);

  useEffect(() => {
    console.log("isOpen", isOpen);
  }, [isOpen]);

  // Initial selected value setup
  useEffect(() => {
    if (selectedEmployee && employees && employees.length > 0) {
      const employee = employees.find((e) => e.id === selectedEmployee);
      if (employee) {
        setSelectedOption({
          value: employee.id,
          label: employee.displayName || employee.email,
          imageUrl: employee.avatar || undefined,
          isPersona: false,
        });
      }
    } else if (selectedPersona && personas && personas.length > 0) {
      const persona = personas.find((p) => p.id === selectedPersona);
      if (persona) {
        setSelectedOption({
          value: persona.id,
          label: persona.name,
          imageUrl: "/icons/light-mode/persona_manager_off.svg",
          isPersona: true,
        });
      }
    } else {
      setSelectedOption(null);
    }
  }, [selectedEmployee, selectedPersona, employees, personas]);

  // Handle outside click to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        menuRef.current &&
        selectorRef.current &&
        !menuRef.current.contains(event.target as Node) &&
        !selectorRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Convert employees to option format
  const employeeOptions = employees
    ? employees.map((employee) => ({
        value: employee.id,
        label: employee.displayName || employee.email,
        imageUrl: employee.avatar || undefined,
        isPersona: false,
      }))
    : [];

  // Map personas to options with static icon
  const personaOptions = personas
    ? personas.map((persona) => ({
        value: persona.id,
        label: persona.name,
        imageUrl: "/icons/light-mode/persona_manager_off.svg",
        isPersona: true,
      }))
    : [];

  const handleToggleMenu = () => {
    console.log("disabled", disabled);
    console.log("enforceHrisIntegration", config.enforceHrisIntegration);
    if (disabled && config.enforceHrisIntegration) {
      setShowTooltip(true);
      return;
    }

    setIsOpen(!isOpen);
  };

  // Hide tooltip when mouse leaves
  const handleMouseLeave = () => {
    setShowTooltip(false);
  };

  const handleSelect = (option: typeof selectedOption) => {
    if (option === null) {
      onChange("", false);
      setSelectedOption(null);
    } else {
      onChange(option.value, option.isPersona);
      setSelectedOption(option);
    }
    setIsOpen(false);
  };

  return (
    <>
      <div
        className={`circle-employee-selector ${
          disabled && config.enforceHrisIntegration ? "disabled" : ""
        }`}
        ref={selectorRef}
        onMouseLeave={handleMouseLeave}>
        <div
          className={`circle-selector ${selectedOption ? "selected" : ""}`}
          onClick={handleToggleMenu}>
          {selectedOption ? (
            <>
              {selectedOption.imageUrl && (
                <img
                  src={selectedOption.imageUrl}
                  alt={selectedOption.label}
                  className="circle-avatar"
                />
              )}
              <span className="selected-label">{selectedOption.label}</span>
              <div
                className="clear-button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleSelect(null);
                }}>
                ×
              </div>
            </>
          ) : null}
        </div>

        {showTooltip && disabled && config.enforceHrisIntegration && (
          <div className="circle-tooltip">HRIS Integration required</div>
        )}

        {isOpen && (
          <div className="circle-dropdown-menu" ref={menuRef}>
            {includeNoFilter && (
              <div className="menu-section">
                <div className="option" onClick={() => handleSelect(null)}>
                  <span>No context</span>
                </div>
              </div>
            )}

            {employeeOptions.length > 0 && (
              <div className="menu-section">
                <div className="section-label">Employees</div>
                {employeeOptions.map((option) => (
                  <div
                    key={option.value}
                    className="option"
                    onClick={() => handleSelect(option)}>
                    {option.imageUrl && (
                      <img
                        src={option.imageUrl}
                        alt={option.label}
                        className="option-avatar"
                      />
                    )}
                    <span>{option.label}</span>
                  </div>
                ))}
              </div>
            )}

            {personaOptions.length > 0 && (
              <div className="menu-section">
                <div className="section-label">Personas</div>
                {personaOptions.map((option) => (
                  <div
                    key={option.value}
                    className="option"
                    onClick={() => handleSelect(option)}>
                    {option.imageUrl && (
                      <img
                        src={option.imageUrl}
                        alt={option.label}
                        className="option-avatar"
                      />
                    )}
                    <span>{option.label}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
};

export default CirclePersonaEmployeeSelect;
